import React, { useState, useMemo, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Users,
  Search,
  Filter,
  Eye,
  Mail,
  Phone,
  MapPin,
  Clock,
  Building2,
  RefreshCw,
  ChevronDown,
  X,
  Ticket,
  AlertCircle,
  CheckCircle2,
  User,
  Briefcase,
  CreditCard,
  TrendingUp,
  ArrowLeft,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "react-toastify";
import eventService from "../../../../api/EventService";
import Loader from "../../../../components/misc/Loader";

/**
 * Conference & Trade Mission Attendees Management Component
 */
const ConferenceTradeAttendees = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // State management
  const [events, setEvents] = useState([]);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [eventTypeFilter, setEventTypeFilter] = useState("all");
  const [paymentStatusFilter, setPaymentStatusFilter] = useState("all");
  const [verificationFilter, setVerificationFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState("asc");
  const [loading, setLoading] = useState(true);
  const [attendeesLoading, setAttendeesLoading] = useState(false);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  // Fetch events
  useEffect(() => {
    fetchEvents();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch attendees when event is selected
  useEffect(() => {
    if (selectedEvent) {
      fetchAttendees(selectedEvent.slug || selectedEvent.event_slug);
    }
  }, [selectedEvent]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const response = await eventService.getEvents();
      if (response?.status && response?.data) {
        let eventsData = [];
        if (response.data.status_code === 200 && response.data.data) {
          eventsData = Array.isArray(response.data.data)
            ? response.data.data
            : [response.data.data];
        } else if (Array.isArray(response.data)) {
          eventsData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          eventsData = response.data.data;
        }

        // Filter for only conferences and trade missions
        const filteredEvents = eventsData.filter((event) => {
          if (!event.event_type || !Array.isArray(event.event_type)) {
            return false;
          }
          return (
            event.event_type.includes("conference") ||
            event.event_type.includes("trade_mission")
          );
        });

        setEvents(filteredEvents);
        
        // Check if there's a pre-selected event from navigation state
        const preSelectedEvent = location.state?.event;
        if (preSelectedEvent && filteredEvents.some(e => (e.slug || e.event_slug) === (preSelectedEvent.slug || preSelectedEvent.event_slug))) {
          setSelectedEvent(preSelectedEvent);
        } else if (filteredEvents.length > 0) {
          setSelectedEvent(filteredEvents[0]);
        }
      } else {
        toast.error(response?.message || "Failed to fetch events");
        setEvents([]);
      }
    } catch (error) {
      console.error("ERROR: ", error);
      toast.error(
        error.response?.data?.reason ||
          "An error occurred while fetching events"
      );
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchAttendees = async (eventSlug) => {
    setAttendeesLoading(true);
    try {
      const response = await eventService.getEventAttendees(eventSlug);
      if (response?.status) {
        let attendeesData = [];
        if (response.data.status_code === 200) {
          // Handle the nested object structure from the API
          const data = response.data.data;
          if (typeof data === "object" && !Array.isArray(data)) {
            // Convert object with numeric keys to array
            attendeesData = Object.values(data);
          } else if (Array.isArray(data)) {
            attendeesData = data;
          }
        } else if (Array.isArray(response.data)) {
          attendeesData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          attendeesData = response.data.data;
        }
        setAttendees(attendeesData);
      } else {
        toast.error(response?.message || "Failed to fetch attendees");
        setAttendees([]);
      }
    } catch (error) {
      console.error("ERROR: ", error);
      toast.error(
        error.response?.data?.reason ||
          "An error occurred while fetching attendees"
      );
      setAttendees([]);
    } finally {
      setAttendeesLoading(false);
    }
  };

  // Check if event is conference
  const isConference = (event) => {
    return event?.event_type?.includes("conference");
  };

  // Handle both DDMMYYYY (current API) and YYYYMMDD (legacy) DOB formats
  const formatDob = useCallback((dobValue) => {
    if (!dobValue) return "N/A";

    const formatDateParts = (day, month, year) => {
      const monthNames = [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ];
      const dayText = String(day).padStart(2, "0");
      return `${dayText}-${monthNames[month - 1]}-${year}`;
    };

    const dobString = String(dobValue).trim();
    if (/^\d{8}$/.test(dobString)) {
      const tryBuildDate = (day, month, year) => {
        const parsedDate = new Date(year, month - 1, day);
        const isValidDate =
          parsedDate.getFullYear() === year &&
          parsedDate.getMonth() === month - 1 &&
          parsedDate.getDate() === day;
        return isValidDate ? formatDateParts(day, month, year) : null;
      };

      // Prefer current API format DDMMYYYY first
      const dmyFormatted = tryBuildDate(
        Number(dobString.slice(0, 2)),
        Number(dobString.slice(2, 4)),
        Number(dobString.slice(4, 8))
      );
      if (dmyFormatted) return dmyFormatted;

      // Fallback for legacy YYYYMMDD
      const ymdFormatted = tryBuildDate(
        Number(dobString.slice(6, 8)),
        Number(dobString.slice(4, 6)),
        Number(dobString.slice(0, 4))
      );
      if (ymdFormatted) {
        return ymdFormatted;
      }
    }

    const fallbackDate = new Date(dobString);
    if (Number.isNaN(fallbackDate.getTime())) return dobString;

    return formatDateParts(
      fallbackDate.getDate(),
      fallbackDate.getMonth() + 1,
      fallbackDate.getFullYear()
    );
  }, []);

  // Get payment status (pending for conferences, completed for trade missions)
  const getPaymentStatus = useCallback((attendee) => {
    const event = attendee.event || selectedEvent;
    if (isConference(event)) {
      return "pending";
    }
    return "completed";
  }, [selectedEvent]);

  // Calculate statistics
  const stats = useMemo(() => {
    const totalAttendees = attendees.length;
    const conferences = events.filter((e) => isConference(e)).length;
    const tradeMissions = events.length - conferences;

    return {
      totalAttendees,
      conferences,
      tradeMissions,
    };
  }, [events, attendees]);

  // Filter and search attendees
  const filteredAttendees = useMemo(() => {
    return attendees
      .filter((attendee) => {
        const user = attendee.user || {};
        const fullName = `${user.first_name || ""} ${user.last_name || ""}`.toLowerCase();
        const email = (user.email || "").toLowerCase();
        const phone = (user.phone_number || "").toLowerCase();
        const location = (user.location || "").toLowerCase();

        const matchesSearch =
          fullName.includes(searchTerm.toLowerCase()) ||
          email.includes(searchTerm.toLowerCase()) ||
          phone.includes(searchTerm.toLowerCase()) ||
          location.includes(searchTerm.toLowerCase());

        const matchesPaymentStatus =
          paymentStatusFilter === "all" ||
          getPaymentStatus(attendee) === paymentStatusFilter;

        const matchesVerification =
          verificationFilter === "all" ||
          (verificationFilter === "verified" &&
            (user.email_verified_at || user.phone_number_verified_at)) ||
          (verificationFilter === "unverified" &&
            !user.email_verified_at &&
            !user.phone_number_verified_at);

        return matchesSearch && matchesPaymentStatus && matchesVerification;
      })
      .sort((a, b) => {
        let aValue, bValue;

        switch (sortBy) {
          case "name":
            aValue = `${a.user?.first_name || ""} ${a.user?.last_name || ""}`.toLowerCase();
            bValue = `${b.user?.first_name || ""} ${b.user?.last_name || ""}`.toLowerCase();
            break;
          case "email":
            aValue = (a.user?.email || "").toLowerCase();
            bValue = (b.user?.email || "").toLowerCase();
            break;
          case "location":
            aValue = (a.user?.location || "").toLowerCase();
            bValue = (b.user?.location || "").toLowerCase();
            break;
          case "verification":
            aValue = a.user?.email_verified_at || a.user?.phone_number_verified_at ? 1 : 0;
            bValue = b.user?.email_verified_at || b.user?.phone_number_verified_at ? 1 : 0;
            break;
          default:
            aValue = a[sortBy] || "";
            bValue = b[sortBy] || "";
        }

        if (sortOrder === "asc") {
          return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
        } else {
          return aValue < bValue ? 1 : aValue > bValue ? -1 : 0;
        }
      });
  }, [attendees, searchTerm, paymentStatusFilter, verificationFilter, sortBy, sortOrder, getPaymentStatus]);

  // Filter events
  const filteredEvents = useMemo(() => {
    return events.filter((event) => {
      if (eventTypeFilter === "all") return true;
      if (eventTypeFilter === "conference") return isConference(event);
      if (eventTypeFilter === "trade_mission") return !isConference(event);
      return true;
    });
  }, [events, eventTypeFilter]);

  // Pagination
  const paginatedAttendees = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAttendees.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAttendees, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAttendees.length / itemsPerPage);

  // Handlers
  const handleViewAttendee = (attendee) => {
    setSelectedAttendee(attendee);
    setShowViewModal(true);
  };

  const handleBack = () => {
    navigate("/admin/media/conference");
  };

  // Get verification status badge
  const getVerificationBadge = (user) => {
    if (user?.email_verified_at || user?.phone_number_verified_at) {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Verified
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
        <AlertCircle className="w-3 h-3 mr-1" />
        Unverified
      </span>
    );
  };

  // Get payment status badge
  const getPaymentStatusBadge = (attendee) => {
    const status = getPaymentStatus(attendee);
    if (status === "pending") {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
          <Clock className="w-3 h-3 mr-1" />
          Pending
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
        <CheckCircle2 className="w-3 h-3 mr-1" />
        Completed
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <Loader fullScreen={true} text="Loading events..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-3 py-4 sm:px-4 sm:py-6 lg:px-6 lg:py-8 space-y-4 sm:space-y-6 lg:space-y-8">
        {/* Header */}
        <div className="flex flex-col space-y-4 sm:flex-row sm:items-center sm:justify-between sm:space-y-0">
          <div className="flex-shrink-0">
            <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold text-gray-900 flex items-center">
              <Briefcase className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 mr-2 sm:mr-3 text-orange-600 flex-shrink-0" />
              <span className="truncate">Conference & Trade Mission Attendees</span>
            </h1>
            <p className="text-sm sm:text-base text-gray-600 mt-1 sm:mt-2">
              Manage attendees for conferences and trade missions
            </p>
          </div>

          <div className="flex flex-col space-y-2 sm:flex-row sm:items-center sm:space-y-0 sm:space-x-3">
            <button
              onClick={fetchEvents}
              className="bg-gray-100 text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg hover:bg-gray-200 active:bg-gray-300 transition-colors flex items-center justify-center min-h-[44px] w-full sm:w-auto touch-manipulation"
            >
              <RefreshCw className="w-4 h-4 mr-2 flex-shrink-0" />
              <span>Refresh</span>
            </button>
            <button
              onClick={handleBack}
              className="bg-white text-gray-700 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border border-gray-300 hover:bg-gray-50 active:bg-gray-100 transition-colors flex items-center justify-center min-h-[44px] w-full sm:w-auto touch-manipulation"
            >
              <ArrowLeft className="w-4 h-4 mr-2 flex-shrink-0" />
              <span>Back</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
          <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-5 lg:p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                  Total Attendees
                </p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-orange-600 mt-1">
                  {stats.totalAttendees}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-orange-100 rounded-lg flex-shrink-0 ml-3">
                <Users className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-orange-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-5 lg:p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                  Conferences
                </p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-blue-600 mt-1">
                  {stats.conferences}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-blue-100 rounded-lg flex-shrink-0 ml-3">
                <Building2 className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-5 lg:p-6 shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300">
            <div className="flex items-center justify-between">
              <div className="min-w-0 flex-1">
                <p className="text-xs sm:text-sm font-medium text-gray-600 truncate">
                  Trade Missions
                </p>
                <p className="text-xl sm:text-2xl lg:text-3xl font-bold text-emerald-600 mt-1">
                  {stats.tradeMissions}
                </p>
              </div>
              <div className="p-2 sm:p-3 bg-emerald-100 rounded-lg flex-shrink-0 ml-3">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-emerald-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Event Selector & Filters */}
        <div className="bg-white rounded-lg sm:rounded-xl p-4 sm:p-6 shadow-sm border border-gray-200">
          {/* Event Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Event
            </label>
            <select
              value={selectedEvent?.slug || selectedEvent?.event_slug || ""}
              onChange={(e) => {
                const event = events.find(
                  (ev) => (ev.slug || ev.event_slug) === e.target.value
                );
                setSelectedEvent(event);
                setCurrentPage(1);
              }}
              className="w-full px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent bg-white"
            >
              {filteredEvents.map((event) => (
                <option key={event.slug || event.event_slug} value={event.slug || event.event_slug}>
                  {event.event_title || event.venue_name} -{" "}
                  {isConference(event) ? "Conference" : "Trade Mission"}
                </option>
              ))}
            </select>
          </div>

          {/* Mobile Filter Toggle */}
          <div className="flex items-center justify-between mb-4 lg:hidden">
            <h3 className="text-base font-semibold text-gray-900 flex items-center">
              <Filter className="w-4 h-4 mr-2 text-orange-600" />
              Filters
            </h3>
            <button
              onClick={() => setShowMobileFilters(!showMobileFilters)}
              className="flex items-center px-3 py-2 text-sm bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors touch-manipulation"
            >
              <span>Filters</span>
              <ChevronDown
                className={`w-4 h-4 ml-2 transition-transform ${
                  showMobileFilters ? "rotate-180" : ""
                }`}
              />
            </button>
          </div>

          {/* Desktop Filter Header */}
          <div className="hidden lg:flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 flex items-center">
              <Filter className="w-5 h-5 mr-2 text-orange-600" />
              Filters & Search
            </h3>
          </div>

          {/* Filter Controls */}
          <div
            className={`space-y-4 ${
              showMobileFilters ? "block" : "hidden lg:block"
            }`}
          >
          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-3 w-4 h-4 sm:w-5 sm:h-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, email, phone, location..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-3 text-sm sm:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            {/* Event Type Filter */}
            <select
              value={eventTypeFilter}
              onChange={(e) => {
                setEventTypeFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 sm:px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
            >
              <option value="all">All Types</option>
              <option value="conference">Conferences Only</option>
              <option value="trade_mission">Trade Missions Only</option>
            </select>

            {/* Payment Status Filter */}
            <select
              value={paymentStatusFilter}
              onChange={(e) => {
                setPaymentStatusFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 sm:px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
            >
              <option value="all">All Payment Status</option>
              <option value="pending">Pending Only</option>
              <option value="completed">Completed Only</option>
            </select>

            {/* Verification Filter */}
            <select
              value={verificationFilter}
              onChange={(e) => {
                setVerificationFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="px-3 sm:px-4 py-3 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white"
            >
              <option value="all">All Verification</option>
              <option value="verified">Verified Only</option>
              <option value="unverified">Unverified Only</option>
            </select>

            {/* Sort Order */}
            <button
              onClick={() => setSortOrder(sortOrder === "asc" ? "desc" : "asc")}
              className="flex items-center justify-center px-3 sm:px-4 py-3 text-sm bg-gray-100 hover:bg-gray-200 active:bg-gray-300 border border-gray-300 rounded-lg transition-colors touch-manipulation"
            >
              {sortOrder === "asc" ? "↑" : "↓"}{" "}
              {sortOrder === "asc" ? "Ascending" : "Descending"}
            </button>
          </div>

          {/* Sort By */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <button
              onClick={() => setSortBy("name")}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                sortBy === "name"
                  ? "bg-orange-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Sort by Name
            </button>
            <button
              onClick={() => setSortBy("email")}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                sortBy === "email"
                  ? "bg-orange-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Sort by Email
            </button>
            <button
              onClick={() => setSortBy("location")}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                sortBy === "location"
                  ? "bg-orange-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Sort by Location
            </button>
            <button
              onClick={() => setSortBy("verification")}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                sortBy === "verification"
                  ? "bg-orange-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              Sort by Verification
            </button>
          </div>
        </div>

        {/* Clear Filters Button */}
        {(searchTerm ||
          eventTypeFilter !== "all" ||
          paymentStatusFilter !== "all" ||
          verificationFilter !== "all") && (
          <div className="mt-4 flex justify-end">
            <button
              onClick={() => {
                setSearchTerm("");
                setEventTypeFilter("all");
                setPaymentStatusFilter("all");
                setVerificationFilter("all");
              }}
              className="px-3 py-2 text-sm text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
            >
              Clear Filters
            </button>
          </div>
        )}
      </div>

      {/* Attendees Display */}
      {attendeesLoading ? (
        <div className="flex justify-center items-center py-12">
          <Loader text="Loading attendees..." />
        </div>
      ) : (
        <>
          {/* Attendees Table/Cards */}
          <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {/* Table Header */}
            <div className="px-3 sm:px-4 lg:px-6 py-3 sm:py-4 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between">
                <h3 className="text-base sm:text-lg font-semibold text-gray-900">
                  Attendee List ({filteredAttendees.length}{" "}
                  {filteredAttendees.length === 1 ? "attendee" : "attendees"})
                </h3>
                <div className="hidden sm:block text-xs sm:text-sm text-gray-500">
                  Page {currentPage} of {totalPages || 1}
                </div>
              </div>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Attendee Details
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Contact Info
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Location
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Verification
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {paginatedAttendees.map((attendee, index) => {
                    const user = attendee.user || {};
                    return (
                      <tr
                        key={attendee.user_slug || index}
                        className="hover:bg-gray-50 transition-colors duration-150"
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-sm">
                              <span className="text-white font-medium text-sm">
                                {user.first_name?.charAt(0) || ""}
                                {user.last_name?.charAt(0) || ""}
                              </span>
                            </div>
                            <div className="ml-4 min-w-0">
                              <div className="text-sm font-semibold text-gray-900 truncate">
                                {user.first_name} {user.last_name}
                              </div>
                              <div className="text-sm text-gray-500 flex items-center truncate">
                                {user.gender && (
                                  <>
                                    <span className="w-2 h-2 bg-gray-300 rounded-full mr-2 flex-shrink-0"></span>
                                    {user.gender.charAt(0).toUpperCase() +
                                      user.gender.slice(1)}
                                  </>
                                )}
                                {user.dob && (
                                  <span className="ml-2 text-gray-400 truncate">
                                    • DOB: {formatDob(user.dob)}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            <div className="text-sm text-gray-900 flex items-center truncate">
                              <Mail className="w-3 h-3 mr-2 text-gray-400 flex-shrink-0" />
                              <span className="truncate">{user.email || "N/A"}</span>
                            </div>
                            <div className="text-sm text-gray-500 flex items-center truncate">
                              <Phone className="w-3 h-3 mr-2 text-gray-400 flex-shrink-0" />
                              <span className="truncate">
                                {user.phone_number || "N/A"}
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center min-w-0">
                            <MapPin className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                            <div className="min-w-0">
                              <div className="text-sm font-medium text-gray-900 truncate">
                                {user.location || "N/A"}
                              </div>
                              <div className="text-sm text-gray-500 truncate">
                                {user.region || "N/A"}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top">
                          {getVerificationBadge(user)}
                          <div className="text-xs text-gray-500 mt-1 whitespace-nowrap">
                            Email {user.email_verified_at ? "✓" : "✗"} | Phone{" "}
                            {user.phone_number_verified_at ? "✓" : "✗"}
                          </div>
                        </td>
                        <td className="px-6 py-4 align-top">
                          {getPaymentStatusBadge(attendee)}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            onClick={() => handleViewAttendee(attendee)}
                            className="p-2 text-gray-400 hover:text-orange-600 transition-colors hover:bg-orange-50 rounded-lg"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden divide-y divide-gray-200">
              {paginatedAttendees.map((attendee, index) => {
                const user = attendee.user || {};
                return (
                  <div
                    key={attendee.user_slug || index}
                    className="p-4 hover:bg-gray-50 transition-colors"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center min-w-0 flex-1">
                        <div className="flex-shrink-0 w-10 h-10 bg-gradient-to-r from-orange-400 to-orange-600 rounded-full flex items-center justify-center shadow-sm mr-3">
                          <span className="text-white font-medium text-sm">
                            {user.first_name?.charAt(0) || ""}
                            {user.last_name?.charAt(0) || ""}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="text-base font-semibold text-gray-900 truncate">
                            {user.first_name} {user.last_name}
                          </div>
                          <div className="flex items-center gap-2 mt-1">
                            {getVerificationBadge(user)}
                            {getPaymentStatusBadge(attendee)}
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewAttendee(attendee)}
                        className="p-2 text-gray-400 hover:text-orange-600 transition-colors hover:bg-orange-50 rounded-lg ml-2"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                      {/* Contact Info */}
                      <div className="space-y-2">
                        <div className="flex items-center text-sm">
                          <Mail className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                          <span className="text-gray-900 truncate">
                            {user.email || "N/A"}
                          </span>
                        </div>
                        <div className="flex items-center text-sm">
                          <Phone className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                          <span className="text-gray-900 truncate">
                            {user.phone_number || "N/A"}
                          </span>
                        </div>
                      </div>

                      {/* Location */}
                      <div className="flex items-center text-sm">
                        <MapPin className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                        <span className="text-gray-900 truncate">
                          {user.location || "N/A"}, {user.region || "N/A"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Empty State */}
            {filteredAttendees.length === 0 && !attendeesLoading && (
              <div className="text-center py-12 lg:py-16">
                <Users className="mx-auto h-12 w-12 lg:h-16 lg:w-16 text-gray-300" />
                <h3 className="mt-4 text-base lg:text-lg font-medium text-gray-900">
                  No attendees found
                </h3>
                <p className="mt-2 text-sm lg:text-sm text-gray-500 max-w-sm mx-auto px-4">
                  {searchTerm ||
                  paymentStatusFilter !== "all" ||
                  verificationFilter !== "all"
                    ? "Try adjusting your search or filter criteria."
                    : selectedEvent
                    ? "No attendees registered for this event yet."
                    : "Please select an event to view attendees."}
                </p>
              </div>
            )}
          </div>

          {/* Pagination */}
          {filteredAttendees.length > 0 && (
            <div className="bg-white rounded-lg sm:rounded-xl p-3 sm:p-4 shadow-sm border border-gray-200">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0">
                <div className="text-xs sm:text-sm text-gray-500 text-center sm:text-left">
                  Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                  {Math.min(currentPage * itemsPerPage, filteredAttendees.length)}{" "}
                  of {filteredAttendees.length} results
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                  >
                    Previous
                  </button>
                  <div className="flex items-center px-3 py-2 text-xs sm:text-sm bg-orange-600 text-white rounded-lg font-medium">
                    {currentPage}
                  </div>
                  <button
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-xs sm:text-sm bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-200 transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* View Attendee Modal */}
      <AnimatePresence>
        {showViewModal && selectedAttendee && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={() => setShowViewModal(false)}
          >
            {/* Backdrop */}
            <div className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm" />

            {/* Modal */}
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="relative bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            >
              {/* Header */}
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur">
                      <User className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-white">
                        Attendee Details
                      </h3>
                      <p className="text-orange-100 text-sm">
                        {selectedEvent?.event_title || "Event"}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowViewModal(false)}
                    className="text-white hover:text-orange-200 p-2 hover:bg-white hover:bg-opacity-20 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content */}
              <div className="overflow-y-auto flex-1 p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Personal Information */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <User className="w-4 h-4 mr-2 text-orange-600" />
                      Personal Information
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Full Name
                        </label>
                        <div className="mt-1 text-sm font-medium text-gray-900">
                          {selectedAttendee.user?.first_name}{" "}
                          {selectedAttendee.user?.last_name}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Gender
                        </label>
                        <div className="mt-1 text-sm font-medium text-gray-900 capitalize">
                          {selectedAttendee.user?.gender || "N/A"}
                        </div>
                      </div>
                      {selectedAttendee.user?.dob && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Date of Birth
                          </label>
                          <div className="mt-1 text-sm font-medium text-gray-900">
                            {formatDob(selectedAttendee.user.dob)}
                          </div>
                        </div>
                      )}
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          User Slug
                        </label>
                        <div className="mt-1 text-xs font-mono bg-gray-100 px-2 py-1 rounded">
                          {selectedAttendee.user_slug}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <Mail className="w-4 h-4 mr-2 text-orange-600" />
                      Contact Information
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Email Address
                        </label>
                        <div className="mt-1 text-sm font-medium text-gray-900 break-all">
                          {selectedAttendee.user?.email || "N/A"}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Phone Number
                        </label>
                        <div className="mt-1 text-sm font-medium text-gray-900">
                          {selectedAttendee.user?.phone_number || "N/A"}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Channel
                        </label>
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-orange-100 text-orange-800 capitalize">
                            {selectedAttendee.user?.channel || "N/A"}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Location Information */}
                  <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <MapPin className="w-4 h-4 mr-2 text-orange-600" />
                      Location Information
                    </h4>
                    <div className="space-y-3">
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Location
                        </label>
                        <div className="mt-1 text-sm font-medium text-gray-900">
                          {selectedAttendee.user?.location || "N/A"}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                          Region
                        </label>
                        <div className="mt-1 text-sm font-medium text-gray-900">
                          {selectedAttendee.user?.region || "N/A"}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Verification Status */}
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-4 border border-green-200">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />
                      Verification Status
                    </h4>
                    <div className="space-y-2">
                      <div
                        className={`flex items-center px-3 py-2 rounded-lg ${
                          selectedAttendee.user?.email_verified_at
                            ? "bg-green-100"
                            : "bg-red-100"
                        }`}
                      >
                        <div
                          className={`w-3 h-3 rounded-full mr-3 ${
                            selectedAttendee.user?.email_verified_at
                              ? "bg-green-500"
                              : "bg-red-500"
                          }`}
                        ></div>
                        <span className="text-sm font-medium">
                          {selectedAttendee.user?.email_verified_at
                            ? "Email Verified"
                            : "Email Not Verified"}
                        </span>
                      </div>
                      <div
                        className={`flex items-center px-3 py-2 rounded-lg ${
                          selectedAttendee.user?.phone_number_verified_at
                            ? "bg-green-100"
                            : "bg-red-100"
                        }`}
                      >
                        <div
                          className={`w-3 h-3 rounded-full mr-3 ${
                            selectedAttendee.user?.phone_number_verified_at
                              ? "bg-green-500"
                              : "bg-red-500"
                          }`}
                        ></div>
                        <span className="text-sm font-medium">
                          {selectedAttendee.user?.phone_number_verified_at
                            ? "Phone Verified"
                            : "Phone Not Verified"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Status */}
                  <div className="bg-gradient-to-r from-orange-50 to-orange-50 rounded-xl p-4 border border-orange-200 lg:col-span-2">
                    <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                      <CreditCard className="w-4 h-4 mr-2 text-orange-600" />
                      Payment Status
                    </h4>
                    <div className="flex items-center justify-between">
                      <div>
                        {getPaymentStatusBadge(selectedAttendee)}
                        <p className="text-sm text-gray-600 mt-2">
                          {isConference(selectedEvent)
                            ? "Conference payment feature is currently pending implementation."
                            : "Trade mission registration is complete."}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Document Information (if available) */}
                  {selectedAttendee.user?.document_type && (
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 lg:col-span-2">
                      <h4 className="font-semibold text-gray-900 mb-4 flex items-center">
                        <Ticket className="w-4 h-4 mr-2 text-orange-600" />
                        Document Information
                      </h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                            Document Type
                          </label>
                          <p className="mt-1 text-sm font-medium text-gray-900 capitalize">
                            {selectedAttendee.user.document_type?.replace(/_/g, " ")}
                          </p>
                        </div>
                        {selectedAttendee.user.card_number && (
                          <div>
                            <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                              Card Number
                            </label>
                            <p className="mt-1 text-sm font-mono text-gray-900">
                              {selectedAttendee.user.card_number}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex justify-end">
                <button
                  onClick={() => {
                    setShowViewModal(false);
                    setSelectedAttendee(null);
                  }}
                  className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
                >
                  Close
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default ConferenceTradeAttendees;